/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  TableCell,
  TableRow,
  TableExpandRow,
  Tag
} from 'carbon-components-react';
import cn from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';

import { VALID_RESPONSE_METHODS } from '../../constants';
import { getTagColor } from '../../helpers';
import { ExpandedEndpointRow } from './ExpandedEndpointRow';
import { getFilteredResponseProperties } from './helpers';

interface EndpointRowProps {
  /**
   *
   */
  headers: any;
  /**
   *
   */
  row: any;
}
export const EndpointRow = ({ headers, row }: EndpointRowProps) => {
  const endpointOperation = row.summary || row.description || row.path;
  const endpointMethod = row.method;
  const endpointResource = row.path;
  const isValidResponse = VALID_RESPONSE_METHODS.includes(endpointMethod);
  const hasExpansion =
    isValidResponse &&
    (!_.isEmpty(row.request_properties) ||
      !_.isEmpty(getFilteredResponseProperties(row.response_properties)));

  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);

  // Function to expand an endpoint row in the datatable
  const expandEndpointRow = () => {
    if (expandedIndexes.includes(row.id)) {
      const expandedIndex = expandedIndexes.indexOf(row.id);
      expandedIndexes.splice(expandedIndex, 1);
      const copyExpandedIndexes = _.cloneDeep(expandedIndexes);
      setExpandedIndexes(copyExpandedIndexes);
    } else {
      const copyExpandedIndexes = _.cloneDeep(expandedIndexes.concat([row.id]));
      setExpandedIndexes(copyExpandedIndexes);
    }
  };

  return (
    <>
      {hasExpansion ? (
        <TableExpandRow
          ariaLabel={row.id}
          id={row.id}
          key={row.id}
          isExpanded={expandedIndexes.includes(row.id)}
          onExpand={() => expandEndpointRow()}>
          <TableCell
            className={cn({
              CatalogOpenApiDataTable__operation: true,
              InvalidResponseMethod__disabled: !isValidResponse
            })}
            key={`${row.id}:Operation`}>
            {endpointOperation}
          </TableCell>
          <TableCell
            className="CatalogOpenApiDataTable__method"
            key={`${row.id}:Method`}>
            <Tag
              type={getTagColor[endpointMethod]}
              className={cn({
                InvalidResponseMethod__disabled: !isValidResponse
              })}>
              {endpointMethod}
            </Tag>
          </TableCell>
          <TableCell
            className={cn({
              CatalogOpenApiDataTable__resource: true,
              InvalidResponseMethod__disabled: !isValidResponse
            })}
            key={`${row.id}:Resource`}>
            {endpointResource}
          </TableCell>
        </TableExpandRow>
      ) : (
        <TableRow id={row.id} key={row.id}>
          <TableCell />
          <TableCell
            className={cn({
              CatalogOpenApiDataTable__operation: true,
              InvalidResponseMethod__disabled: !isValidResponse
            })}
            key={`${row.id}:Operation`}>
            {endpointOperation}
          </TableCell>
          <TableCell
            className="CatalogOpenApiDataTable__method"
            key={`${row.id}:Method`}>
            <Tag
              type={getTagColor[endpointMethod]}
              className={cn({
                InvalidResponseMethod__disabled: !isValidResponse
              })}>
              {endpointMethod}
            </Tag>
          </TableCell>
          <TableCell
            className={cn({
              CatalogOpenApiDataTable__resource: true,
              InvalidResponseMethod__disabled: !isValidResponse
            })}
            key={`${row.id}:Resource`}>
            {endpointResource}
          </TableCell>
        </TableRow>
      )}
      {hasExpansion && expandedIndexes.includes(row.id) && (
        <ExpandedEndpointRow headers={headers} row={row} />
      )}
    </>
  );
};

export default EndpointRow;
